import {
  action,
  autorun,
  observable,
  useStrict
} from 'mobx';

import {
  observer
} from 'mobx-react';

import AppState from '../AppState';

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min)) + min;
}

function generatePositionsArray(width, height, safeRadius, irregularity) {
  // declarations
  const positionsArray = [];
  // count the amount of rows and columns
  const maxX = width - safeRadius;
  const maxY = height - safeRadius;
  const minX = safeRadius;
  const minY = safeRadius;
  const rows = Math.floor((maxY - minY) / safeRadius);
  const columns = Math.floor((maxX - minY) / safeRadius);
  // loop through rows
  for (let r = 1; r <= rows; r += 1) {
      // loop through columns
      for (let c = 1; c <= columns; c += 1) {
          // populate array with point object
          positionsArray.push({
              x: Math.round((maxX - minX) * c / columns) + (minX / 2) + getRandomInt(irregularity * -1, irregularity),
              y: Math.round((maxY - minY) * r / rows) + (minY / 2) + getRandomInt(irregularity * -1, irregularity)
          });
      }
  }
  // return array
  return positionsArray;
}

function initItems() {
  const items = [];
  if (AppState.data) {
    let slideIndex = 0;
    AppState.data.sections.topTen.lists.forEach((list) => {
      if (list.appearsInCloud) {
        list.items.forEach((item, itemIndex) => {
          const nextItemIndex = itemIndex + 1;
          const prevItemIndex = itemIndex - 1;
          items.push({
            ...item,
            position: {
              x: 0,
              y: 0,
              z: "",
            },
            itemIndex: itemIndex,
            listTitle: list.navHeading,
            listTitleMain: item.label,
            listItemsCount: list.items.length,
            nextItemIndex: nextItemIndex < list.items.length ? slideIndex + 1 : null,
            prevItemIndex: prevItemIndex >= 0 ? slideIndex - 1   : null,
            entering: false,
            entered: false,
            twinkling: false,
          });
          slideIndex++;
        });
      }
    });
  }
  return items;
}

useStrict();

const IntroCloudState = {
  items: initItems(),
  activeItemIndex: null,
  positions: [],
  activeFilterOptionsTrend: [],
  activeFilterOptionsMonth: [],
  dragging: false,
  showHelper: true,
  helperText: "Drag",
  offsetX: 0,
  offsetY: 0,
  minItemY: 500,
  maxItemY: -500,
  minItemX: 500,
  maxItemX: -500,
  minItemZ: 0,
  maxItemZ: 100,
  introActive: true,

  get containerHeight() {
    return AppState.windowHeight - (3 * AppState.rem);
  },

  get containerWidth() {
    return AppState.windowWidth;
  },

  get height() {
    return AppState.windowHeight * 2 * 4;
  },

  get width() {
    return AppState.windowWidth * 2 * 4;
  },

  get maxY() {
    return 0;
  },

  get minY() {
    return (this.height - this.containerHeight) * -1;
  },

  get maxX() {
    return 0;
  },

  get minX() {
    return (this.width - this.containerWidth) * -1;
  },

  get initialY() {
    return ((this.maxY - this.minY) * 0.5) + this.minY;
  },

  get initialX() {
    return ((this.maxX - this.minX) * 0.5) + this.minX;
  },

  get itemRadius() {
    return Math.sqrt((this.width * this.height) / this.items.length) * 0.75;
  },

  get thumbnailArea() {
    return (this.height * this.width) * 0.001;
  },

  get grid() {
    const {
      windowWidth,
      windowHeight
    } = AppState;

    const area = windowWidth * windowHeight;
    const density = 1 / 20000;
    const items = area * density;

    return items;
  },

  get filterOptionsColour() {
    const colours = [];
    this.items.forEach((item) => {
      if (!item.colour) return;
      if (colours.indexOf(item.colour) === -1) colours.push(item.colour);
    });
    return colours.sort();
  },

  get filterOptionsMonth() {
    return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November'];
  },

  get filterOptionsTrend() {
    const trends = [];
    this.items.forEach((item) => {
      if (!item.trend) return;
      item.trend.forEach((trend) => {
        if (trends.indexOf(trend) === -1) trends.push(trend);
      })
    });
    return trends.sort();
  },

  get resultsAreFiltered() {
    return this.activeFilterOptionsMonth.length > 0 || this.activeFilterOptionsTrend.length > 0;
  },

  get activeItem() {
    return this.activeItemIndex !== null ? this.items[this.activeItemIndex] : null;
  },

  toggleFilter: action(function(filter, option) {
    const index = this[`activeFilterOptions${filter}`].indexOf(option);
    if (index === -1) {
      this[`activeFilterOptions${filter}`].push(option)
    } else {
      this[`activeFilterOptions${filter}`].splice(index, 1);
    }
  }),

  clearFilters: action(function() {
    this.activeFilterOptionsMonth = [];
    this.activeFilterOptionsTrend = [];
  }),

  generatePositions: action(function(width, height) {
    this.positions = generatePositionsArray(width, height, this.itemRadius, this.itemRadius / 2);
  }),

  getRandomPosition: action(function() {
    const randomIndex = getRandomInt(0, this.positions.length - 1);
    const coordinates = this.positions[randomIndex];
    this.positions.splice(randomIndex, 1);
    return coordinates;
  }),

  setItemPosition: action(function(itemIndex) {
    this.items[itemIndex].position = this.getRandomPosition();
    this.items[itemIndex].position.z = ['front', 'middle', 'back'][Math.floor(Math.random() * 3)];
  }),

  activateItem: action(function(itemIndex) {
    this.activeItemIndex = itemIndex;
  }),

  deactivateItem: action(function() {
    this.activeItemIndex = null;
  }),

  twinkleItem: action(function(itemIndex) {
    this.items[itemIndex].twinkling = !this.items[itemIndex].twinkling;
  })
};

autorun(function() {
  IntroCloudState.generatePositions(IntroCloudState.width, IntroCloudState.height);
});

export default observer(observable(IntroCloudState));



// WEBPACK FOOTER //
// ./src/states/IntroCloudState/index.js